export const kioskDevicesResource = {
  name: 'Kiosks',
  fields: {
    id: 'ID',
    siteId: 'Premises',
    siteName: 'Premises',
    name: 'Name',
    status: 'Status',
    apiKeySuffix: 'API Key',
    nearbyChargerIds: 'Nearby Chargers',
    octopusDeviceId: 'Octopus Device ID',
    locationLabel: 'Location',
    lastHeartbeatAt: 'Last Heartbeat',
    health: 'Health',
    appVersion: 'App Version',
    launcherVersion: 'Launcher Version',
    updateState: 'Update State',
    updateStateAt: 'Update State Since',
    updateError: 'Update Error',
    settlementDueSince: 'Settlement Due Since',
    createdAt: 'Created At',
    updatedAt: 'Updated At',
    maintenanceEnabled: 'Maintenance mode',
  },
  status: { pending: 'Pending enrollment', active: 'Active', disabled: 'Disabled' },
  maintenance: {
    chip: 'Maintenance',
    switchLabel: 'Maintenance mode',
    helperText: 'Locks this kiosk on the maintenance screen until switched off. Charging in progress continues.',
    enabled: 'Maintenance mode switched on',
    disabled: 'Maintenance mode switched off',
  },
  health: {
    reader: 'Octopus reader', terminal: 'Card terminal', printer: 'Printer', paper: 'Paper', clock: 'Clock',
    ok: 'OK', absent: 'Not configured', error: 'Error', low: 'Low', out: 'Out', unknown: 'Unknown',
    skewMs: 'Clock skew %{ms} ms', offline: 'Offline', online: 'Online', neverSeen: 'Never connected',
  },
  sections: { basicInfo: 'Basic Information', hardware: 'Hardware', status: 'Status', settings: 'Settings', enrollment: 'Enrollment' },
  list: { subtitle: 'Self-service charging kiosks and their health' },
  create: {
    title: 'Register Kiosk',
    subtitle: 'Create a kiosk record and get its one-time enrollment code',
    siteHelperText: 'Only premises with kiosk access enabled are listed',
    nameHelperText: 'Shown in the admin console and on receipts, e.g. "Lobby kiosk"',
    nearbyChargersHelperText: 'Chargers closest to this kiosk, listed first on the screen',
    octopusDeviceIdHelperText: 'Optional. The reader device ID appears here after enrollment if left blank',
    locationLabelHelperText: 'Where the kiosk stands, shown to drivers collecting an Octopus refund, e.g. "Level B1, next to the lift lobby" (up to 80 characters)',
  },
  edit: {
    title: 'Kiosk',
    issueCode: 'Issue new enrollment code',
    issueCodeConfirm: 'This revokes the kiosk\'s current credentials and sends every Octopus refund still waiting at this kiosk to customer service. The kiosk must be re-enrolled with the new code. Continue?',
    codeIssued: 'Enrollment code issued',
    disable: 'Disable kiosk',
    enable: 'Enable kiosk',
  },
  enrollment: {
    title: 'Enrollment code',
    body: 'Enter this code on the kiosk. It is shown only once and expires in 24 hours.',
    expires: 'Expires %{when}',
    copy: 'Copy code',
    copied: 'Code copied',
    done: 'Done',
  },
  update: {
    title: 'Update',
    targetVersion: 'Target version',
    waitingBecause: 'Waiting because',
    waitingNotBefore: 'Not before the rollout start',
    ocrlReleaseVersion: 'Octopus library release',
    idle: 'Up to date',
    retry: 'Retry update',
    retried: 'The update is offered again',
    retryRefused: {
      not_retryable: 'Only a failed or rolled back update can be retried.',
      rollout_not_active: 'Its rollout is paused or has ended. Resume a paused rollout first, or create a new rollout for this kiosk.',
    },
    restartApp: 'Restart app',
    restartPc: 'Restart PC',
    restartAppConfirm: 'The kiosk restarts the app as soon as nobody is using it and no payment is in progress. Charging is not affected.',
    restartPcConfirm: 'The kiosk restarts the PC as soon as nobody is using it and no payment is in progress. Charging is not affected. The kiosk is out of service for a few minutes.',
    restartRequested: 'Restart requested',
    restartPending: 'Restart requested %{time}; waits for an idle moment (expires after 24 hours)',
    restartPendingNoTime: 'Restart requested; waits for an idle moment (expires after 24 hours)',
    restartInProgress: 'Restart in progress',
    inTestGroup: 'In the test group',
    reader: 'Octopus reader',
    readerStates: { ready: 'Ready', connecting: 'Reconnecting', error: 'Error', stopped: 'Stopped', none: 'Not reported' },
    readerReported: 'Reported %{time}',
    readerNotReady: 'The Octopus reader is reconnecting. Restart now restarts without waiting for the reader. Wait for the reader restarts once it has reconnected. Either way the kiosk waits until nobody is using it and no payment is in progress.',
    restartNow: 'Restart now',
    waitForReader: 'Wait for the reader',
    pendingWaitsForReader: 'This restart also waits for the Octopus reader to reconnect.',
    retriesVersion: 'Retries %{version}.',
    releaseKey: 'Release key',
    releaseKeyNone: 'No release key pinned. This kiosk installs no updates until someone pins the key on its admin screen.',
    releaseKeyCurrent: '(current key)',
    releaseKeyOther: '(not the current key)',
    lastRestart: {
      title: 'Last restart',
      types: { restart: 'App restart', reboot: 'PC restart' },
      statuses: { pending: 'Waiting for an idle moment', in_progress: 'In progress', completed: 'Done', failed: 'Failed', cancelled: 'Cancelled', expired: 'Expired' },
      requested: 'Requested %{time}',
      closed: 'Closed %{time}',
      expired: 'The kiosk was never idle in the 24 hours after the request, so the restart did not happen.',
      reasons: {
        not_permitted: 'The launcher refused the restart because this kiosk is not set up to allow it.',
        launcher_unavailable: 'The launcher could not be reached, so the restart did not happen.',
        command_failed: 'The launcher ran the restart command and the command failed.',
      },
    },
  },
  settings: {
    title: 'Settings',
    key: 'Setting',
    value: 'Value',
    source: 'Source',
    authority: 'Authority',
    scope: { default: 'Default', system: 'System', site: 'Premises', kiosk: 'This kiosk' },
    authorityClass: { cloud: 'Console', device: 'Device' },
    override: 'Override for this kiosk',
    clear: 'Clear kiosk override',
    saved: 'Setting saved',
    cleared: 'Override cleared',
    invalid: 'Invalid value',
    jsonHint: 'Enter the value as JSON (numbers, true/false, "text", [lists] or {objects})',
    kioskScopeNotAllowed: 'Set by support',
  },
  octopusClear: {
    title: 'Octopus blocked at this kiosk',
    explain: 'The reader was sent a deduct and the link or power dropped before it answered, so the kiosk cannot tell whether the customer was charged. Octopus stays unavailable at this kiosk until you clear it.',
    evidence: 'Before you clear, look up each receipt number in the reader log on the kiosk PC (the reader-logs folder) and in the Octopus daily transaction report for this reader.',
    chargedFollowUp: 'If the reader log or report shows the deduct, clear it as Charged. The refund owed then appears in Kiosk Transactions as "Expired: result unknown". When customer service has refunded the customer, mark that row refunded by hand there.',
    noBlocked: 'The kiosk reports no blocked Octopus payments.',
    receipt: 'Receipt number %{receipt}',
    transaction: 'Transaction %{id}',
    noTransaction: 'No transaction id',
    waiting: 'Waiting for the kiosk since %{time}',
    stale: 'The kiosk has not run the clear sent at %{time} within 24 hours. Cancel it or send a new clear.',
    button: 'Clear blocked Octopus payment',
    confirm: 'The kiosk marks the selected payments as cleared by staff and makes Octopus available again. It keeps its records.',
    finding: { not_charged: 'Not charged: the reader log and report show no deduct', charged: 'Charged: the reader log or report shows the deduct' },
    status: { pending: 'Waiting', in_progress: 'In progress', completed: 'Done', cancelled: 'Cancelled', expired: 'Expired' },
    note: 'What you checked',
    noteHelper: 'For example: reader log 2026-09-30, no deduct for this receipt number',
    submit: 'Clear',
    sent: 'Sent to the kiosk',
    failed: 'Could not send the clear',
    cancel: 'Cancel clear',
    cancelled: 'Clear cancelled',
    notOpen: 'This clear is no longer waiting: the kiosk ran it, or it was cancelled or expired',
    cancelFailed: 'Could not cancel the clear',
    loadFailed: 'Could not load the Octopus clears: %{_}',
    clearedRow: 'Cleared receipt number %{receipt}, HK$%{amount}',
    skipped: { not_found: 'The kiosk has no payment %{attempt}', not_blocked: 'Payment %{attempt} was no longer blocked' },
  },
  printLog: {
    title: 'Receipt printer log',
    columns: { time: 'Time', slip: 'Slip', result: 'Result', reason: 'Reason', receipt: 'Receipt' },
    kind: { session_slip: 'Session slip', receipt: 'Receipt', refund_incomplete: 'Refund not completed', test: 'Test print' },
    copy: 'Copy',
    outcome: { printed: 'Printed', partly_printed: 'Partly printed', not_printed: 'Not printed' },
    empty: 'No print jobs yet',
    loadMore: 'Load more',
    loadFailed: 'Could not load the print log: %{error}',
  },
  printerDetail: {
    paper_low: 'Paper low',
    paper_out: 'Out of paper',
    cover_open: 'Cover open',
    cutter_error: 'Cutter jammed',
    overheat: 'Print head too hot',
    mechanical_error: 'Print head lever raised',
    unrecoverable_error: 'Printer error, switch it off and on',
    printer_error: 'Printer error',
    offline: 'Offline',
    not_answering: 'Not answering',
    not_found: 'Not found on USB',
    ambiguous: 'More than one printer found, set printer.address',
    transport_unsupported: 'Printer connection type not supported',
    write_failed: 'Sending to the printer failed',
    write_timeout: 'The printer did not take the slip in time',
    slip_failed: 'The slip could not be built',
    job_timeout: 'The printer did not finish in time',
    printedLine: 'Printer: %{detail} (checked %{time})',
  },
  readerPanel: {
    title: 'Octopus reader',
    noReport: 'No reader report yet',
    state: 'Reader state',
    deviceNumber: 'Device number',
    tat: 'TAT number',
    tatReported: 'Reported: %{value}',
    tatConfigured: 'Set in settings: %{value}',
    tatNotReported: 'not reported',
    tatNotSet: 'not set',
    logUpload: 'Log upload',
    logOk: 'OK',
    logFailing: 'Failing',
    timeZone: 'Clock time zone',
    timeZoneOk: 'OK (%{offset})',
    timeZoneWrong: 'Wrong (%{offset})',
    timeZoneNotReported: 'Not reported',
  },
  errors: {
    KIOSK_OCTOPUS_CLEAR_REFUSED: 'The kiosk is not active, a clear is already waiting, or the kiosk no longer reports this payment as blocked',
    KIOSK_SITE_NOT_ENABLED: 'Enable kiosk access on the premises first',
    KIOSK_NOT_FOUND: 'Kiosk not found',
    KIOSK_SETTING_INVALID_VALUE: 'The value is not valid for this setting',
    KIOSK_SETTING_SCOPE_NOT_ALLOWED: 'This setting cannot be set at this level',
    KIOSK_QUOTE_TTL_BELOW_PAYMENT_WAIT: 'The quote lifetime must be at least the payment wait plus 130 seconds.',
  },
  lastSettlement: { label: 'Last settlement', none: 'No settlement yet', view: 'View settlements', outcomes: { ok: 'Settled', failed: 'Failed', interrupted: 'Interrupted' } },
};
export const kioskDevices = kioskDevicesResource;
